// Library Methods
import { useCallback, useEffect, useState } from "react";

// Utils
import {
  isValidColorVisionReport,
  isValidVisualAcuityReport,
  isValidPelliRobsonReport,
  isValidVisualFieldReport,
  isValidCSTumblingEReport,
  isValidRAPDReport,
  isValidOcularMotilityReport,
  isValidLensSimulationReport,
  isValidVideoReport,
  isValidSynoptophoreReport,
} from "src/utils/exams/examReportsHelper";

// Types
import {
  EnrichedRetinaLogikSectionType,
  ExamCommentsType,
  ProgressionAnalysisType,
} from "src/services/exams/types";

// Hooks
import useEnrichedProgressionAnalysis from "./useEnrichedProgressionAnalysis";
import { useProgressionAnalysisComments } from "src/services/exams/hooks/progression-reports/useProgressionAnalysisComments";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type UseProgressionAnalysisValidationType = {
  analysisId: string;
  patientId: string;
  analysisData: ProgressionAnalysisType | undefined;
};

const useProgressionAnalysisValidation = ({
  analysisData,
  analysisId,
  patientId,
}: UseProgressionAnalysisValidationType) => {
  const { clinicInfo } = useClinicContext();

  const [commentData, setCommentData] = useState<Array<ExamCommentsType>>([]);

  const [isValidating, setIsValidating] = useState(true);
  const [hasRunValidation, setHasRunValidation] = useState(false);
  const [shouldDisplayReport, setShouldDisplayReport] = useState(false);

  const { mutateAsync: reportCommentsMutate } = useProgressionAnalysisComments(
    clinicInfo?.id
  );

  const getComments = useCallback(async () => {
    const comments = await reportCommentsMutate({
      patientId,
      progressionAnalysisId: analysisId,
    });
    setCommentData([...comments]);
  }, [patientId, analysisId, reportCommentsMutate]);

  // fetch comments on page load
  useEffect(() => {
    if (!clinicInfo?.id) return;

    getComments();
  }, [clinicInfo?.id, getComments]);

  const { enrichedExams, enrichedExternalExams } =
    useEnrichedProgressionAnalysis({
      patientId,
      analysisData,
    });

  // whether the report should be displayed or not
  const validateAnalysisExams = useCallback(
    (
      analysis: ProgressionAnalysisType,
      analysisExams: Array<EnrichedRetinaLogikSectionType>
    ) => {
      if (!analysis || !analysisExams) return false;
      if (analysis.state === "Invalid") return false;

      // array that checks if all retinalogik exams in the analysis are valid
      const boolArray: Array<boolean> = [];

      // assume that all external exams are valid
      const retinaLogikExams = analysisExams.filter(
        (exam) => exam.isRetinaLogikExam
      );

      retinaLogikExams.forEach((examSection) => {
        const report = examSection?.reportData;

        const isIncompleteExam = examSection.completionState !== "Completed";
        const isInvalidExam = examSection.completionState === "Invalid";

        if (isInvalidExam || isIncompleteExam) {
          // add to the boolean array
          boolArray.push(false);
          return;
        }

        const isValidColorVision = isValidColorVisionReport(report);
        const isValidVisualAcuity = isValidVisualAcuityReport(report);
        const isValidPelliRobson = isValidPelliRobsonReport(report);
        const isValidCSTumblingE = isValidCSTumblingEReport(report);
        const isValidVisualField = isValidVisualFieldReport(report);
        const isValidRAPD = isValidRAPDReport(report);
        const isValidOcularMotility = isValidOcularMotilityReport(report);
        const isValidSynoptophore = isValidSynoptophoreReport(report);
        const isValidLensSimulation = isValidLensSimulationReport(report);
        const isValidVideo = isValidVideoReport(report);

        if (
          isValidColorVision ||
          isValidVisualAcuity ||
          isValidPelliRobson ||
          isValidCSTumblingE ||
          isValidVisualField ||
          isValidRAPD ||
          isValidOcularMotility ||
          isValidSynoptophore ||
          isValidLensSimulation ||
          isValidVideo
        ) {
          // add to the boolean array
          boolArray.push(true);
          return;
        }

        // add to the boolean array
        boolArray.push(false);
        return;
      });

      // return if all exams are valid
      return boolArray.every((val) => val === true);
    },
    []
  );

  useEffect(() => {
    if (
      analysisData?.exams?.length === enrichedExams?.length &&
      analysisData?.externalExams?.length === enrichedExternalExams?.length &&
      !hasRunValidation
    ) {
      setShouldDisplayReport(
        validateAnalysisExams(analysisData, enrichedExams)
      );
      setIsValidating(false);
      setHasRunValidation(true);
    }
  }, [
    validateAnalysisExams,
    analysisData,
    enrichedExams,
    enrichedExternalExams,
    hasRunValidation,
  ]);

  return {
    isValidating,
    shouldDisplayReport,
    enrichedExams,
    enrichedExternalExams,
    commentData,
    getComments,
  };
};

export default useProgressionAnalysisValidation;
